/**
 * Shared line-smoothing for the dashboard's trend charts.
 *
 * Monotone-cubic (Fritsch–Carlson) interpolation — the curve passes
 * through every point and never overshoots between them, so peaks don't
 * wiggle or fold. Extracted from the Performance "Web Vitals over time"
 * chart so the Traffic and Spaces/Explore charts draw identical curves.
 *
 * Pair the paths with `vectorEffect="non-scaling-stroke"` on the SVG
 * <path> so the stroke width + round caps render in screen space and
 * don't skew under a non-uniform `preserveAspectRatio="none"` stretch.
 */
export interface CurvePoint {
  x: number;
  y: number;
}

/** Fritsch–Carlson monotone tangents for the given points. Guarantees
 *  the interpolant stays monotone on each interval (no overshoot). */
export function monotoneTangents(pts: ReadonlyArray<CurvePoint>): number[] {
  const n = pts.length;
  if (n < 2) return pts.map(() => 0);
  const h: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = pts[i + 1]!.x - pts[i]!.x;
    h[i] = dx;
    slope[i] = dx === 0 ? 0 : (pts[i + 1]!.y - pts[i]!.y) / dx;
  }
  const m: number[] = new Array(n);
  m[0] = slope[0]!;
  m[n - 1] = slope[n - 2]!;
  for (let i = 1; i < n - 1; i++) {
    const s0 = slope[i - 1]!;
    const s1 = slope[i]!;
    if (s0 * s1 <= 0) {
      m[i] = 0;
    } else {
      // Weighted harmonic mean of adjacent slopes.
      const w1 = 2 * h[i]! + h[i - 1]!;
      const w2 = h[i]! + 2 * h[i - 1]!;
      m[i] = (w1 + w2) / (w1 / s0 + w2 / s1);
    }
  }
  return m;
}

/** One cubic Bézier segment p1→p2 given the monotone tangents at each
 *  end. Use when each segment needs its own stroke (e.g. band-coloured
 *  perf curve). */
export function monotoneSegment(p1: CurvePoint, p2: CurvePoint, m1: number, m2: number): string {
  const dx = (p2.x - p1.x) / 3;
  const c1x = p1.x + dx;
  const c1y = p1.y + m1 * dx;
  const c2x = p2.x - dx;
  const c2y = p2.y - m2 * dx;
  return `M${p1.x.toFixed(1)},${p1.y.toFixed(1)} C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
}

/** Whether a sparkline series is long + dense enough to collapse to
 *  weekly buckets. Shared by the Performance tile bars and the Traffic
 *  KPI bars so the two pages bucket identically across date ranges:
 *  daily up to ~3 months, weekly beyond once there are enough points. */
export function shouldBucketSparkbarsWeekly(rangeDays: number, count: number): boolean {
  return rangeDays >= 90 && count > 28;
}

/** A single smooth monotone-cubic path (`M…C…C…`) through the points.
 *  Empty string for fewer than two points. Use for a one-colour line. */
export function smoothPath(pts: ReadonlyArray<CurvePoint>): string {
  if (pts.length < 2) return '';
  const m = monotoneTangents(pts);
  let d = `M${pts[0]!.x.toFixed(1)},${pts[0]!.y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const dx = (p2.x - p1.x) / 3;
    d += ` C${(p1.x + dx).toFixed(1)},${(p1.y + m[i]! * dx).toFixed(1)} ${(p2.x - dx).toFixed(1)},${(p2.y - m[i + 1]! * dx).toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }
  return d;
}
